import { Box, Button, Container, Typography, Stack, Paper } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ForumIcon from '@mui/icons-material/Forum';
import PeopleIcon from '@mui/icons-material/People';
import SpeedIcon from '@mui/icons-material/Speed';
import { motion } from "framer-motion";

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
        
        {/* Hero Section */}
        <Box sx={{ 
            pt: 12, 
            pb: 12, 
            background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, rgba(147, 51, 234, 0.1) 100%)',
            position: 'relative',
            overflow: 'hidden'
        }}>
            <Container maxWidth="lg">
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 8, alignItems: 'center' }}>
                    <Box sx={{ flex: 1 }}>
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                        <Typography variant="h2" fontWeight={800} gutterBottom sx={{ background: 'linear-gradient(90deg, #2563eb, #9333ea)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                            Where Communities Come Alive
                        </Typography>
                        <Typography variant="h5" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
                            Join Threadly to discuss topics that matter to you. Connect with thousands of members in real-time.
                        </Typography>
                        <Stack direction="row" spacing={2}>
                            <Button variant="contained" size="large" onClick={() => navigate("/register")} sx={{ px: 4, py: 1.5, borderRadius: 3, fontSize: '1.1rem', background: 'linear-gradient(135deg, #2563eb, #9333ea)' }}>
                                Get Started
                            </Button>
                            <Button variant="outlined" size="large" onClick={() => navigate("/login")} sx={{ px: 4, py: 1.5, borderRadius: 3, fontSize: '1.1rem' }}>
                                Sign In
                            </Button>
                        </Stack>
                        </motion.div>
                    </Box>
                    <Box sx={{ flex: 1, width: '100%' }}>
                         <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }}>
                            <Box sx={{ 
                                width: '100%', 
                                height: 400, 
                                borderRadius: 8, 
                                bgcolor: 'background.paper',
                                boxShadow: '0 20px 80px rgba(147, 51, 234, 0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                position: 'relative'
                            }}>
                                <img src="/forum_hero_illustration.png" alt="Community" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.9 }} 
                                     onError={(e) => (e.currentTarget.src = 'https://source.unsplash.com/random/800x600?abstract')} />
                                
                                {/* Floating Element 1: Message Bubble */}
                                <Box component={motion.div} 
                                    animate={{ y: [0, -15, 0] }}
                                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                                    sx={{ position: 'absolute', top: 40, left: 40, bgcolor: 'background.paper', p: 1.5, borderRadius: 3, boxShadow: 3, maxWidth: 160 }}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                         <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: 'primary.light' }} />
                                         <Box>
                                             <Box sx={{ width: 80, height: 8, bgcolor: 'text.disabled', borderRadius: 1, mb: 0.5, opacity: 0.3 }} />
                                             <Box sx={{ width: 50, height: 8, bgcolor: 'text.disabled', borderRadius: 1, opacity: 0.2  }} />
                                         </Box>
                                    </Stack>
                                </Box>

                                {/* Floating Element 2: Like Badge */}
                                <Box component={motion.div} 
                                    animate={{ y: [0, 10, 0] }}
                                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                                    sx={{ position: 'absolute', bottom: 60, right: 30, bgcolor: 'background.paper', px: 2, py: 1, borderRadius: 4, boxShadow: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: 'error.light', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'error.main', fontSize: 14 }}>♥</Box>
                                    <Typography variant="body2" fontWeight={700}>1.2k</Typography>
                                </Box>

                                {/* Floating Element 3: Topic Tag */}
                                <Box component={motion.div} 
                                    animate={{ scale: [1, 1.05, 1], rotate: [0, 5, 0] }}
                                    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                                    sx={{ position: 'absolute', top: '50%', right: '50%', transform: 'translate(50%, -50%)', bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)', px: 3, py: 1, borderRadius: 8 }}>
                                    <Typography variant="h6" fontWeight={800} sx={{ color: 'white', letterSpacing: 1 }}>#COMMUNITY</Typography>
                                </Box>
                            </Box>
                        </motion.div>
                    </Box>
                </Box>
            </Container>
        </Box>

        {/* Features */}
        <Container maxWidth="lg" sx={{ py: 12 }}>
            <Typography variant="h3" fontWeight={800} textAlign="center" sx={{ mb: 8 }}>
                Why Threadly?
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 4 }}>
                {[
                    { icon: <ForumIcon fontSize="large" color="primary" />, title: "Topic Based", desc: "Organized discussions centered around topics you care about." },
                    { icon: <PeopleIcon fontSize="large" color="secondary" />, title: "Vibrant Community", desc: "Connect with like-minded individuals from around the world." },
                    { icon: <SpeedIcon fontSize="large" color="success" />, title: "Real-time Votes", desc: "Instant feedback on your contributions with our voting system." },
                ].map((f, i) => (
                    <Box key={i}>
                         <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} viewport={{ once: true }} style={{ height: '100%' }}>
                        <Paper variant="outlined" sx={{ p: 4, borderRadius: 4, height: '100%', border: 'none', bgcolor: 'action.hover', transition: '0.3s', '&:hover': { transform: 'translateY(-8px)' } }}>
                            <Box sx={{ mb: 2 }}>{f.icon}</Box>
                            <Typography variant="h5" fontWeight={700} gutterBottom>{f.title}</Typography>
                            <Typography variant="body1" color="text.secondary">{f.desc}</Typography>
                        </Paper>
                        </motion.div>
                    </Box>
                ))}
            </Box>
        </Container>

        {/* Creative Section: Explore Topics */}
        <Box sx={{ py: 12, bgcolor: 'background.paper' }}>
            <Container maxWidth="lg">
                <Typography variant="h3" fontWeight={800} textAlign="center" sx={{ mb: 2 }}>
                    Dive into your interests
                </Typography>
                <Typography variant="h6" color="text.secondary" textAlign="center" sx={{ mb: 8, maxWidth: 600, mx: 'auto' }}>
                    From technology to arts, find the communities that spark your curiosity.
                </Typography>
                
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 3 }}>
                    {[
                        { label: 'Technology', color: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)' },
                        { label: 'Design', color: 'linear-gradient(135deg, #d946ef 0%, #9333ea 100%)' },
                        { label: 'Lifestyle', color: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)' },
                        { label: 'Startups', color: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' },
                        { label: 'Education', color: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' },
                        { label: 'Game Development', color: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)' },
                        { label: 'Programming', color: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)' },
                        { label: 'Mobile Development', color: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)' },
                    ].map((topic, i) => (
                        <Box key={i} component={motion.div} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                            <Paper 
                                onClick={() => navigate(`/feed?topic=${encodeURIComponent(topic.label)}`)}
                                elevation={0}
                                sx={{ 
                                    p: 4, 
                                    borderRadius: 4, 
                                    textAlign: 'center', 
                                    cursor: 'pointer',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: topic.color,
                                    color: 'white',
                                    transition: 'all 0.3s ease',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                    '&:hover': {
                                        boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                                        filter: 'brightness(1.1)'
                                    }
                                }}>
                                <Typography variant="h6" fontWeight={700} sx={{ textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>{topic.label}</Typography>
                            </Paper>
                        </Box>
                    ))}
                </Box>
            </Container>
        </Box>

        {/* CTA Section */}
        <Box sx={{ py: 16, textAlign: 'center', bgcolor: 'background.default', position: 'relative', overflow: 'hidden' }}>
             {/* Decorative blurry circles */}
             <Box sx={{ position: 'absolute', top: -100, left: '10%', width: 300, height: 300, bgcolor: 'primary.main', borderRadius: '50%', filter: 'blur(80px)', opacity: 0.1 }} />
             <Box sx={{ position: 'absolute', bottom: -100, right: '10%', width: 200, height: 200, bgcolor: 'secondary.main', borderRadius: '50%', filter: 'blur(60px)', opacity: 0.1 }} />
             
            <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
                <Typography variant="h2" fontWeight={900} gutterBottom>
                    Ready to join the conversation?
                </Typography>
                <Typography variant="h5" color="text.secondary" sx={{ mb: 6 }}>
                    Create an account today and start sharing your ideas with the world.
                </Typography>
                <Button 
                    variant="contained" 
                    size="large" 
                    onClick={() => navigate("/register")} 
                    sx={{ px: 8, py: 2.5, borderRadius: 12, fontSize: '1.2rem', fontWeight: 700, boxShadow: '0 20px 40px rgba(37, 99, 235, 0.3)' }}
                >
                    Create Free Account
                </Button>
            </Container>
        </Box>

        {/* Footer */}
        <Box sx={{ py: 6, borderTop: 1, borderColor: 'divider', textAlign: 'center' }}>
             <Typography variant="body2" color="text.secondary">
                © 2026 Threadly. All rights reserved.
            </Typography>
        </Box>
    </Box>
  );
}
